import { AqdbBatch } from '../../../../aqdb/models/aqdb-batch';
import { TranslationService } from '../../../services/translation.service';
import { MultiLineValueTypes } from '../../cell-renderers/multi-line-value-types';
import { CommonColDef, MultiLineColDef } from '../../column-defs';
import { ColumnType } from '../../column-type';

export class AqdbCoatingAndMagneticCharacteristicsColumnDefinition {
	readonly AQDB_COATING_AND_MAGNETIC_CHARACTERISTICS_CONFIGURATION:
	Map<string, CommonColDef<AqdbBatch> | CommonColDef<AqdbBatch, string>>;

	constructor(private readonly translationService: TranslationService) {
		this.AQDB_COATING_AND_MAGNETIC_CHARACTERISTICS_CONFIGURATION =
		new Map<string, CommonColDef<AqdbBatch> | CommonColDef<AqdbBatch, string>>(
			[
				[
					'metalCoatingCharacteristics',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.metal.coating.characteristics'),
						colId: 'metalCoatingCharacteristics',
						hide: true,
						sortable: false,
					})
				],
				[
					'spotNumber',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.metal.coating.measure.type'),
						fields: ['segments.metalCoatingList.spotNumber'],
						colId: 'spotNumber',
						groupId: 'metalCoatingCharacteristics',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.metalCoatingList)
									.map(metalCoating => metalCoating.spotNumber).join(';') : '';
						},
					})
				],
				[
					'metalCoatingFace',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.coating.surface.face'),
						fields: ['segments.metalCoatingList.face'],
						colId: 'metalCoatingFace',
						groupId: 'metalCoatingCharacteristics',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.metalCoatingList)
									.map(metalCoating => metalCoating.face).join(';') : '';
						},
					})
				],
				[
					'border1Quantity',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.metal.coating.quantity.edge1'),
						fields: ['segments.metalCoatingList.border1Quantity'],
						colId: 'border1Quantity',
						groupId: 'metalCoatingCharacteristics',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.metalCoatingList)
									.map(metalCoating => metalCoating.border1Quantity).join(';') : '';
						},
					})
				],
				[
					'border2Quantity',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.metal.coating.quantity.edge2'),
						fields: ['segments.metalCoatingList.border2Quantity'],
						colId: 'border2Quantity',
						groupId: 'metalCoatingCharacteristics',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.metalCoatingList)
									.map(metalCoating => metalCoating.border2Quantity).join(';') : '';
						},
					})
				],
				[
					'centerQuantity',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.metal.coating.quantity.centre'),
						fields: ['segments.metalCoatingList.centerQuantity'],
						colId: 'centerQuantity',
						groupId: 'metalCoatingCharacteristics',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.metalCoatingList)
									.map(metalCoating => metalCoating.centerQuantity).join(';') : '';
						},
					})
				],
				[
					'averageQuantity',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.metal.coating.average.quantity'),
						fields: ['segments.metalCoatingList.averageQuantity'],
						colId: 'averageQuantity',
						groupId: 'metalCoatingCharacteristics',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.metalCoatingList)
									.map(metalCoating => metalCoating.averageQuantity).join(';') : '';
						},
					})
				],
				[
					'unit',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.unit'),
						fields: ['segments.metalCoatingList.unit'],
						colId: 'unit',
						groupId: 'metalCoatingCharacteristics',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.metalCoatingList)
									.map(metalCoating => metalCoating.unit).join(';') : '';
						},
					})
				],
				[
					'roughnessCharacteristics',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.roughness.characteristics'),
						colId: 'roughnessCharacteristics',
						hide: true,
						sortable: false,
					})
				],
				[
					'roughnessFace',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.coating.surface.face'),
						fields: ['segments.roughnessList.face'],
						colId: 'roughnessFace',
						groupId: 'roughnessCharacteristics',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.roughnessList)
									.map(roughness => roughness.face).join(';') : '';
						},
					})
				],
				[
					'valueRa',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.roughness.ra'),
						fields: ['segments.roughnessList.valueRa'],
						colId: 'valueRa',
						groupId: 'roughnessCharacteristics',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.roughnessList)
									.map(roughness => roughness.valueRa).join(';') : '';
						},
					})
				],
				[
					'valueW',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.roughness.w'),
						fields: ['segments.roughnessList.valueW'],
						colId: 'valueW',
						groupId: 'roughnessCharacteristics',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.roughnessList)
									.map(roughness => roughness.valueW).join(';') : '';
						},
					})
				],
				[
					'cutOff',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.roughness.cut.off'),
						fields: ['segments.roughnessList.cutOff'],
						colId: 'cutOff',
						groupId: 'roughnessCharacteristics',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.roughnessList)
									.map(roughness => roughness.cutOff).join(';') : '';
						},
					})
				],
				[
					'organicCoatingCharacteristics',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.organic.coating.characteristics'),
						colId: 'organicCoatingCharacteristics',
						hide: true,
						sortable: false,
					})
				],
				[
					'organicCoatingFace',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.coating.surface.face'),
						fields: ['segments.organicCoatingList.face'],
						colId: 'organicCoatingFace',
						groupId: 'organicCoatingCharacteristics',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.organicCoatingList)
									.map(organicCoating => organicCoating.face).join(';') : '';
						},
					})
				],
				[
					'chemicalSurfaceTreatmentCharacteristics',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.chemical.surface.treatment'),
						colId: 'chemicalSurfaceTreatmentCharacteristics',
						hide: true,
						sortable: false,
					})
				],
				[
					'chemicalSurfaceTreatmentFace',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.coating.surface.face'),
						fields: ['segments.chemicalSurfaceTreatmentList.face'],
						colId: 'chemicalSurfaceTreatmentFace',
						groupId: 'chemicalSurfaceTreatmentCharacteristics',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.chemicalSurfaceTreatmentList)
									.map(chemicalSurfaceTreatment => chemicalSurfaceTreatment.face).join(';') : '';
						},
					})
				],
				[
					'magneticCharacteristics',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.magnetic.characteristicList'),
						colId: 'magneticCharacteristics',
						hide: true,
						sortable: false,
					})
				],
				[
					'camaTypmth',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.measure.type'),
						fields: ['segments.magneticCharacteristicList.camaTypmth'],
						colId: 'camaTypmth',
						groupId: 'magneticCharacteristics',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.magneticCharacteristicList)
									.map(magnetic => magnetic.camaTypmth).join(';') : '';
						},
					})
				],
				[
					'losses1T',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.losses.1t'),
						fields: ['segments.magneticCharacteristicList.losses1T'],
						colId: 'losses1T',
						groupId: 'magneticCharacteristics',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.magneticCharacteristicList)
									.map(magnetic => magnetic.losses1T).join(';') : '';
						},
					})
				],
				[
					'losses1T5',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.losses.15t'),
						fields: ['segments.magneticCharacteristicList.losses1T5'],
						colId: 'losses1T5',
						groupId: 'magneticCharacteristics',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.magneticCharacteristicList)
									.map(magnetic => magnetic.losses1T5).join(';') : '';
						},
					})
				],
				[
					'induction2500w',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.induction.2500w'),
						fields: ['segments.magneticCharacteristicList.induction2500w'],
						colId: 'induction2500w',
						groupId: 'magneticCharacteristics',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.magneticCharacteristicList)
									.map(magnetic => magnetic.induction2500w).join(';') : '';
						},
					})
				],
				[
					'induction5000w',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.induction.5000w'),
						fields: ['segments.magneticCharacteristicList.induction5000w'],
						colId: 'induction5000w',
						groupId: 'magneticCharacteristics',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.magneticCharacteristicList)
									.map(magnetic => magnetic.induction5000w).join(';') : '';
						},
					})
				],
				[
					'induction10000W',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.induction.10000w'),
						fields: ['segments.magneticCharacteristicList.induction10000W'],
						colId: 'induction10000W',
						groupId: 'magneticCharacteristics',
						columnType: ColumnType.NUMBER_COLUMN,
						valueType: MultiLineValueTypes.NUMBER,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments.flatMap(item => item.magneticCharacteristicList)
									.map(magnetic => magnetic.induction10000W).join(';') : '';
						},
					})
				]
			]
		);
	}
}
